<script lang="ts" setup>
  import {useI18n} from 'vue-i18n';
  import {computed, ref} from 'vue';
  import useCpBem from '~/composables/useCpBem';
  import CpText from '~/components/cp/CpText/CpText.vue';
  import CpModal from '~/components/cp/CpModal/CpModal.vue';
  import CpSectionHeader from '~/components/cp/CpSection/CpSectionHeader/CpSectionHeader.vue';
  import CpModalDataEntryProductQty from '~/components/cp/CpModal/CpModalDataEntryProductQty/CpModalDataEntryProductQty.vue';

  const show = defineModel<boolean>('show', {default: false});
  const props = withDefaults(defineProps<{
    kind?: 'stock'|'warranty'|'return'|'openedPackage'|'notReceivedProduct'
    stock: number
    isDigital?: boolean
    hideLowStockLabel?: boolean
  }>(), {
    kind: 'stock',
  });

  const emit = defineEmits<{
    (e: 'update:show', value: boolean): void
    (e: 'selected', value: number): void
  }>();

  const {b, em} = useCpBem('cp-modal-selection-product-qty');
  const {t} = useI18n();

  const numParts = ref(1);
  const showMoreFive = ref<boolean>(false);
  const pieces = computed(() => props.stock >= 6 ? 6 : props.stock);

  const clickQty = (num: number) => {
    numParts.value = num;
    if (num === 6) {
      showMoreFive.value = true;
      show.value = false;
      numParts.value = 1;
      return;
    }

    show.value = false;
    numParts.value = 1;
    emit('selected', num);
  };

  const title = {
    stock: t('selectQuantityOfPieces'),
    warranty: t('selectQuantityOfPiecesToRequestWarranty'),
    return: t('selectQuantityOfPiecesToRequestReturn'),
    openedPackage: t('selectQuantityOfPiecesWidthOpenPacking'),
    notReceivedProduct: t('selectQuantityOfPiecesThanYouDidNotReceive'),
  };
</script>
<template>
    <div :class="b">
        <CpModal v-model:show="show" @close-modal="show = false">
            <CpSectionHeader
                :title="title[kind]"
                type="h3"
                button="close"
                @header-action="show = false"
            />
            <div
                v-for="num in pieces"
                :key="num"
                :class="em(`option-container`,{selected: num === numParts })"
                data-testid="amount-options"
                @click="clickQty(num)"
            >
                <CpText variant="body-regular">
                    {{ num===6 ? t('moreFiveParts') : t('numParts', num ) }}
                </CpText>
            </div>
        </CpModal>
        <CpModalDataEntryProductQty
            v-model:show="showMoreFive"
            :is-digital="!!isDigital"
            :stock="stock"
            :hide-low-stock-label="hideLowStockLabel"
            @add-qty="$emit('selected', $event.qty)"
        />
    </div>
</template>
<style scoped lang="scss">
  @import "assets/style/global.scss";
  .cp-modal-selection-product-qty {
    &__option-container {
      width: 100%;
      padding: 12px $cp-gutter;
      border-bottom: $cp-border-standard;
      cursor: pointer;
      &--selected {
        background-color: var(--color-neutral-extra-light);
      }
    }
  }
</style>
<i18n locale="es-US" lang="yaml">
  numParts: '1 pieza | {n} piezas'
  moreFiveParts: 'Más de 5 piezas'
  piecesRemain: '¡Solo queda 1 pieza! | ¡Solo quedan {n} piezas!'
  selectQuantityOfPieces: 'Seleccionar cantidad de piezas'
  selectQuantityOfPiecesToRequestWarranty: 'Selecciona la cantidad de piezas a solicitar garantía'
  selectQuantityOfPiecesToRequestReturn: 'Selecciona la cantidad de piezas a solicitar devolución'
  selectQuantityOfPiecesWidthOpenPacking: 'Selecciona la cantidad de piezas con empaque abierto'
  selectQuantityOfPiecesThanYouDidNotReceive: 'Selecciona la cantidad de piezas que no recibiste'
</i18n>
